import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";

import TestHeader from "../components/Test/TestHeader";
import TestFooter from "../components/Test/TestFooter";
import QuestionColumn from "../components/Test/QuestionColumn";
import ContextColumn from "../components/Test/ContextColumn";
import ColumnDivider from "../components/Test/ColumnDivider";
import DesmosCalculatorWindow from "../components/Test/DesmosCalculatorWindow";
import ReferenceWindow from "../components/Test/ReferenceWindow";

import useTestTimer from "../hooks/useTestTimer";
import useQuestionTiming from "../hooks/useQuestionTiming";
import useResizableColumns from "../hooks/useResizableColumns";
import useDiagnosticAutosave from "../hooks/useDiagnosticAutosave";
import useDiagnosticHydration from "../hooks/useDiagnosticHydration";

import { nextAdaptiveQuestion } from "../utils/diagnostics/nextAdaptiveQuestion";
import { submitEnglishDiagnostic } from "../utils/diagnostics/submitEnglishDiagnostic";
import { submitMathDiagnostic } from "../utils/diagnostics/submitMathDiagnostic";

import "../styles/testLayout.css";

const submitBySection = {
  english: submitEnglishDiagnostic,
  math: submitMathDiagnostic,
};

const DiagnosticQuestionPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { state } = useLocation();

  const definition = state?.definition ?? null;
  const progress = state?.progress ?? null;
  const allQuestions = state?.questions ?? [];

  const [sequence, setSequence] = useState([]);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [responses, setResponses] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [pendingAnswer, setPendingAnswer] = useState(null);
  const [autosaveDisabled, setAutosaveDisabled] = useState(false);

  const [showCalculator, setShowCalculator] = useState(false);
  const [showReference, setShowReference] = useState(false);

  const currentIndex = sequence.length;
  const isThankYouPage =
    isSubmitted || currentIndex >= definition?.administeredCount;

  const {
    containerRef,
    leftWidth,
    rightWidth,
    startResize,
  } = useResizableColumns({
    initial: 50,
    min: 25,
    max: 75,
  });

  const { timeRemaining, setTimeRemaining } = useTestTimer();
  const [elapsedSeconds, setElapsedSeconds] = useState(0);


  useQuestionTiming(allQuestions, currentIndex, {});

  const { saveNow, markComplete } = useDiagnosticAutosave({
    userUid: user?.uid,
    diagnosticId: definition?.id,
    estimatedMinutes: definition?.estimatedMinutes,
    timeRemaining,
    disabled: autosaveDisabled,
  });

  useDiagnosticHydration({
    userUid: user?.uid,
    diagnosticId: definition?.id,
    definition,
    setSequence,
    setResponses,
    setCurrentQuestionId,
    setIsHydrated,
    setTimeRemaining,
  });

  const normalizedQuestions = useMemo(() => {
    return allQuestions.map((q) => ({
      id: q.id,
      questionText: q.question,
      choices: q.answerChoices,
      passage: q.passage || null,
      context: q.context || null,
      imageUrl: q.imageUrl || null,
      correctAnswer: q.answer?.correct ?? null,
    }));
  }, [allQuestions]);

  const currentQuestion = useMemo(() => {
    return normalizedQuestions.find(
      (q) => q.id === currentQuestionId
    ) ?? null;
  }, [normalizedQuestions, currentQuestionId]);

  const handleExit = () => {
    navigate("/student");
  };

  const handleAnswer = async (letter) => {
    if (!currentQuestion || isSubmitted) return;

    setPendingAnswer(letter);
  };

  const handleNextAdaptive = async () => {
    if (!currentQuestion || pendingAnswer == null || isSubmitted) return;

    const answeredId = currentQuestion.id;
    const correct =
      currentQuestion.correctAnswer != null
        ? pendingAnswer === currentQuestion.correctAnswer
        : null;

    const nextResponses = {
      ...responses,
      [answeredId]: { answer: pendingAnswer, correct },
    };

    const nextSequence = [...sequence, answeredId];

    const nextQ = nextAdaptiveQuestion({
      definition,
      sequence: nextSequence,
      responses: nextResponses,
    });

    // Always save the last answer
    await saveNow({
      sequence: nextSequence,
      responses: nextResponses,
    });

    // Update local state
    setResponses(nextResponses);
    setSequence(nextSequence);
    setCurrentQuestionId(nextQ);
    setPendingAnswer(null);

    // ---- FINAL SUBMIT PATH
    if (!nextQ) {
      setAutosaveDisabled(true);
      await markComplete();

      const sectionKey = definition.section.toLowerCase();
      const submitFn = submitBySection[sectionKey];

      if (!submitFn) {
        console.error("Unknown diagnostic section:", definition.section);
        return;
      }

      await submitFn({
        userUid: user.uid,
        diagnosticAttempt: {
          responses: nextResponses,
          sequence: nextSequence,
        },
      });

      setIsSubmitted(true);
    }
  };

  if (!definition || !isHydrated) {
    return <div>Loading diagnostic…</div>;
  }

  return (
    <div
      ref={containerRef}
      className="test-page"
      style={{
        "--left-col": `${leftWidth}%`,
        "--right-col": `${rightWidth}%`,
      }}
    >
      <TestHeader
        title={definition.name}
        section={definition.section}
        timeRemaining={timeRemaining}
        onExit={handleExit}
        onToggleCalculator={() => setShowCalculator((p) => !p)}
        onToggleReference={() => setShowReference((p) => !p)}
        isCalculatorOpen={showCalculator}
        isReferenceOpen={showReference}
      />

      {showReference && (
        <ReferenceWindow onClose={() => setShowReference(false)} />
      )}

      {showCalculator && (
        <DesmosCalculatorWindow onClose={() => setShowCalculator(false)} />
      )}

      {isThankYouPage ? (
        <div
          className="test-main"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 48,
          }}
        >
          <div style={{ textAlign: "center", maxWidth: 600 }}>
            <h2>Thanks for completing the diagnostic!</h2>
            <p style={{ marginTop: 16 }}>
              Your results are being processed. You can return to your dashboard.
            </p>
          </div>
        </div>
      ) : (
        <div className="test-main">
          <ContextColumn
            passage={currentQuestion?.passage}
            context={currentQuestion?.context}
            imageUrl={currentQuestion?.imageUrl}
          />

          <ColumnDivider onMouseDown={startResize} />

          <QuestionColumn
            question={currentQuestion}
            questionIndex={currentIndex}
            totalQuestions={definition.administeredCount}
            selectedAnswer={pendingAnswer}
            isMarked={false}
            isReadOnly={false}
            setAnswer={handleAnswer}
            toggleMark={undefined}
            isDiagnostic={true}
          />
        </div>
      )}
      <TestFooter
        currentIndex={currentIndex}
        totalQuestions={definition.administeredCount}
        setCurrentIndex={undefined}
        answers={[]}
        marked={[]}
        isReviewPage={false}
        isReadOnly={false}
        onSubmit={handleNextAdaptive}
        onExit={handleExit}
        isDiagnostic={true}
        canAdvance={pendingAnswer != null}
      />
    </div>
  );
};

export default DiagnosticQuestionPage;
